import { adminRoutes } from "routes/routes";

type PermissionRequirement = string[];

const gamificationPermission = (pathname: string): PermissionRequirement => {
  if (pathname.includes("/progression")) return ["gamification.eloTiers.view"];
  if (pathname.includes("/actions")) return ["gamification.actions.view"];
  if (pathname.includes("/streaks")) return ["gamification.streaks.view"];
  if (pathname.includes("/rewards")) {
    return ["gamification.rewardDefinitions.view"];
  }
  if (pathname.includes("/fulfilment")) return ["gamification.fulfilment.view"];
  if (pathname.includes("/history")) return ["gamification.configHistory.view"];
  return ["gamification.overview.view"];
};

/**
 * Client-side route visibility mirrors the server's permission middleware.
 * The server remains authoritative; this mapping only prevents presenting a
 * page that the current effective-permission snapshot cannot open.
 */
export const requiredAdminPermissionsForPath = (
  pathname: string,
): PermissionRequirement | null => {
  pathname = pathname.split(/[?#]/)[0].replace(/\/$/, "") || "/";
  if (pathname === adminRoutes.referrals)
    return [
      "referrals.records.view",
      "referrals.programme.view",
      "referrals.reports.view",
    ];
  if (pathname === `${adminRoutes.referrals}/programme`)
    return ["referrals.programme.view"];
  if (pathname === `${adminRoutes.referrals}/reports`)
    return ["referrals.reports.view"];
  if (
    pathname === `${adminRoutes.referrals}/records` ||
    pathname === `${adminRoutes.referrals}/review` ||
    /^\/referrals\/users\/[1-9]\d*$/.test(pathname)
  )
    return ["referrals.records.view"];
  if (pathname === adminRoutes.dashboard) return ["stats.dashboard.view"];
  if (pathname.startsWith(adminRoutes.cashback))
    return ["billCashback.campaigns.view"];
  if (pathname.startsWith(adminRoutes.assistant))
    return [
      "assistant.settings.view",
      "assistant.feedback.read",
      "assistant.transcripts.read",
    ];
  if (pathname.startsWith(adminRoutes.accessControl)) {
    return [
      "auditLogs.logs.view",
      "adminManagement.roles.view",
      "adminManagement.admins.view",
      "adminManagement.invitations.view",
      "adminManagement.permissions.view",
    ];
  }
  if (pathname.startsWith(adminRoutes.approvals))
    return ["approvals.requests.view"];
  if (pathname.startsWith("/balances/user/"))
    return ["balances.userBalance.view"];
  if (pathname.startsWith(adminRoutes.balances))
    return ["balances.overview.view"];
  if (pathname.startsWith(adminRoutes.explorer)) return ["users.wallet.view"];
  if (pathname.startsWith("/transactions/user/"))
    return ["users.transactions.view"];
  if (pathname.startsWith("/transactions/risk-control")) {
    return ["transactions.riskReview.view"];
  }
  if (pathname.startsWith("/transactions/admin-market")) {
    return ["transactions.adminMarket.view"];
  }
  if (pathname.startsWith("/transactions/vouchers")) {
    return ["transactions.vouchers.view"];
  }
  if (pathname.startsWith("/transactions/missed-transaction")) {
    return ["transactions.restoreMissedTransaction.view"];
  }
  if (pathname.startsWith(adminRoutes.transactions))
    return ["transactions.search.view"];
  if (pathname.startsWith(adminRoutes.devices)) return ["users.devices.view"];
  if (pathname.startsWith(adminRoutes.users)) return ["users.profile.view"];
  if (pathname.startsWith(adminRoutes.virtualCards))
    return ["virtualCard.cards.view"];
  if (pathname.startsWith(adminRoutes.addressVerificationReviews)) {
    return ["compliance.addressReviews.view"];
  }
  if (pathname === "/merchants/assign") return ["merchants.directory.edit"];
  if (/^\/merchants\/[^/]+\/limit$/.test(pathname))
    return ["merchants.limits.view"];
  if (/^\/merchants\/[^/]+\/transactions$/.test(pathname))
    return ["merchants.transactions.view"];
  if (/^\/merchants\/[^/]+\/analytics$/.test(pathname))
    return ["merchants.transactionAnalytics.view"];
  if (pathname.startsWith(adminRoutes.merchants))
    return ["merchants.directory.view"];
  if (pathname.startsWith(adminRoutes.deposits))
    return ["merchants.deposits.view"];
  if (pathname.startsWith("/analytics/user/")) {
    return [
      "balances.userBalance.view",
      "users.revenue.view",
      "users.transactionAnalytics.view",
    ];
  }
  if (pathname.startsWith("/analytics/transactions")) {
    return ["analytics.transactionAnalytics.view"];
  }
  if (pathname.startsWith("/analytics/acquisitions")) {
    return ["analytics.acquisitions.view"];
  }
  if (pathname.startsWith("/analytics/growth-metrics")) {
    return ["analytics.growth.view"];
  }
  if (pathname.startsWith(adminRoutes.analytics))
    return ["analytics.revenue.view"];
  if (pathname.startsWith(adminRoutes.rates))
    return ["transactions.rates.view"];
  if (pathname.startsWith(adminRoutes.creditUser))
    return ["transactions.creditUser.create"];
  if (pathname.startsWith(adminRoutes.testSms)) {
    return ["growthComms.testSmsVerification.view"];
  }
  if (pathname.startsWith(adminRoutes.notifyAll))
    return ["users.notifyAll.create"];
  if (pathname.startsWith(adminRoutes.paymentOperations))
    return [
      "transactions.walletConfig.view",
      "transactions.operationalBalanceThresholds.view",
      "transactions.systemSettings.edit",
    ];
  if (pathname.startsWith(adminRoutes.paymentMerchant)) {
    return ["transactions.paymentMerchant.view"];
  }
  if (pathname.startsWith(adminRoutes.btcBalance))
    return ["analytics.btcBalance.view"];
  if (pathname === "/bitoshi-hour/create")
    return ["bitoshiHour.sessions.create"];
  if (/^\/bitoshi-hour\/[^/]+\/edit$/.test(pathname))
    return ["bitoshiHour.sessions.edit"];
  if (pathname.startsWith(adminRoutes.bitoshiHour))
    return ["bitoshiHour.sessions.view"];
  if (pathname.startsWith(adminRoutes.otcTransactions))
    return ["otc.transactions.view"];
  if (
    pathname.startsWith(adminRoutes.eddReviews) ||
    pathname.startsWith(adminRoutes.kybReviews)
  )
    return ["users.kyc.view"];
  if (pathname.startsWith(adminRoutes.withdrawalReviews))
    return [
      "transactions.withdrawalReviews.view",
      "transactions.withdrawalReviewRules.view",
    ];
  if (pathname.startsWith(adminRoutes.flaggedTransactions)) {
    return ["transactions.withdrawalReviews.view"];
  }
  if (pathname.startsWith(adminRoutes.settlements)) {
    return ["settlements.settlements.view"];
  }
  if (pathname.startsWith(adminRoutes.gamification)) {
    return gamificationPermission(pathname);
  }
  return [];
};

export const hasAnyPermission = (
  grantedPermissions: string[] | null,
  requiredPermissions: string[] | null,
) =>
  requiredPermissions === null ||
  Boolean(
    grantedPermissions &&
    requiredPermissions.some((permission) =>
      grantedPermissions.includes(permission),
    ),
  );
